import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    Typography,
    TextField,
    MenuItem,
    IconButton,
    FormControl,
    InputLabel,
    Checkbox,
} from "@mui/material";
import { Close, Add } from "@mui/icons-material";
import DynamicFormModal from "./DynamicModalForm";
import { Edit, Search, Refresh, Print } from "@mui/icons-material";
import EnhancedTable from "../common/EnhancedTable";
import { fetchLoanTypeUrl, fetchLoanTypeByIdUrl } from "../../services/masters/LoanTypesService";
import { fetchMemberByMemberNumber, removeIntroducer } from "../../services/masters/memberService";

// EMI calculation function
const calculateEMI = (principal, annualInterestRate, tenureMonths) => {
    const monthlyRate = annualInterestRate / (12 * 100);
    if (monthlyRate === 0) return principal / tenureMonths;
    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths) / (Math.pow(1 + monthlyRate, tenureMonths) - 1);
    return Math.round(emi * 100) / 100; // Round to 2 decimal places
};

const IssueLoanModalForm = ({ open, onClose, onSave }) => {
    const [initialForm] = useState({
        loanType: "loan",
        loanNo: "",
        loanCode: "",
        type: "",
        date: "",
        memberNo: "",
        memberName: "",
        loanAmount: "",
        interestRate: "",
        installments: "",
        installmentAmount: "",
        bondNumber: "",
        penalInterest: "",
        loanBalance: "",
        purpose: "",
        status: "New",
        rebate: "",
        lateFees: "",
        accountStatus: "Pending",
        loanAgainstDepositNo: "",
        // Introducer Details
        introducer: "",
        introducerMobile: "",
        introducerRelation: "",
        depositNo: "",
        amount: "",
        paymentNo: "",
        paymentMode: "CASH",
        chequeNo: "",
        chequeDate: "",
        narration: "",
        otherRemarks: "",
    });

    const [loanTypes, setLoanTypes] = useState([]);
    const [selectedLoanType, setSelectedLoanType] = useState(null);
    const [form, setForm] = useState(initialForm);

    // Introducer state
    const [introducerList, setIntroducerList] = useState([]);
    const [isLoadingIntroducer, setIsLoadingIntroducer] = useState(false);
    const [currentIntroducerId, setCurrentIntroducerId] = useState("");

    useEffect(() => {
        const fetchLoanTypes = async () => {
            try {
                const response = await fetchLoanTypeUrl();
                if (response.data && response.data.data) {
                    setLoanTypes(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching loan types:", error);
            }
        };
        fetchLoanTypes();
    }, []);


    const depositData = [
        // { id: 1, fdNo: "FD1001", type: "Fixed", amount: "50000", int: "6%", matDate: "12/12/2026" },
    ];
    const depositColumns = [
        { id: "fdNo", label: "FD.No" },
        { id: "type", label: "Type" },
        { id: "amount", label: "Amount" },
        { id: "int", label: "Int." },
        { id: "matDate", label: "Mat.Date" },
    ];

    // Handle introducer input change - detects if it's a number and calls API
    const handleIntroducerChange = async (value) => {
        if (!value || value.trim() === '') {
            setForm(prev => ({
                ...prev,
                introducer: ""
            }));
            setCurrentIntroducerId("");
            return;
        }

        // Check if the input is a number (member number)
        const isNumber = /^\d+$/.test(value.trim());

        if (!isNumber) {
            // If it's not a number, treat it as a manual name entry
            setForm(prev => ({
                ...prev,
                introducer: value
            }));
            setCurrentIntroducerId("");
            return;
        }

        // It's a number - check if we should call the API
        // Only call API if mobile field is empty
        if (form.introducerMobile && form.introducerMobile.trim() !== '') {
            // Mobile field has a value, don't call API
            setForm(prev => ({
                ...prev,
                introducer: value // Just store the number
            }));
            return;
        }

        setIsLoadingIntroducer(true);

        try {
            const response = await fetchMemberByMemberNumber(value);

            if (response.data.statuscode === 200 && response.data.data) {
                // Introducer found - populate details
                const introducerData = response.data.data;

                setForm(prev => ({
                    ...prev,
                    introducer: introducerData.fullName || "",
                    introducerMobile: introducerData.mobileNo || introducerData.mobile || "",
                    introducerRelation: prev.introducerRelation || "" // Keep existing relation if any
                }));

                // Store the introducer's ID for API calls
                setCurrentIntroducerId(introducerData.id || "");
            } else {
                // Introducer not found - allow manual entry
                setForm(prev => ({
                    ...prev,
                    introducer: "",
                    introducerMobile: prev.introducerMobile || "",
                    introducerRelation: prev.introducerRelation || ""
                }));
                setCurrentIntroducerId("");
            }
        } catch (error) {
            console.error('Error fetching introducer data');

            if (error.response?.status === 404) {
                // Introducer not found - allow manual entry
                setForm(prev => ({
                    ...prev,
                    introducer: "",
                    introducerMobile: prev.introducerMobile || "",
                    introducerRelation: prev.introducerRelation || ""
                }));
                setCurrentIntroducerId("");
            } else {
                console.error("Error fetching introducer data. Please try again.");
            }
        } finally {
            setIsLoadingIntroducer(false);
        }
    };

    // Add introducer to list
    const addIntroducerToList = async () => {
        if (!form.introducer) {
            return;
        }

        // Check if introducer already exists in the list
        const exists = introducerList.some(intro =>
            (currentIntroducerId && intro.introducerNo === form.introducer) ||
            (!currentIntroducerId && intro.introducerName.toLowerCase() === form.introducer.toLowerCase())
        );

        if (exists) {
            return;
        }

        // Add to local list
        setIntroducerList(prev => [...prev, {
            introducerNo: currentIntroducerId ? form.introducer : "", // Only keep Number if it was a linked member
            introducerName: form.introducer,
            introducerMobile: form.introducerMobile,
            introducerRelation: form.introducerRelation, // Saving the value code
            introducerId: currentIntroducerId || null // Null if manual
        }]);

        // Clear the form fields
        setForm(prev => ({
            ...prev,
            introducer: "",
            introducerMobile: "",
            introducerRelation: ""
        }));
        setCurrentIntroducerId("");
    };

    // Remove introducer from list
    const removeIntroducerFromList = async (index) => {
        const introducerToRemove = introducerList[index];

        if (!introducerToRemove.introducerId) {
            console.error("Cannot remove introducer: Missing introducer ID");
            return;
        }

        try {
            // Prepare the data for API call to deactivate the introducer
            const introducerData = {
                id: introducerToRemove.introducerId,
                active: false
            };

            // Make API call to remove/deactivate introducer
            console.log("Calling Remove Introducer API with data:", introducerData.id);
            const response = await removeIntroducer(introducerData);
            console.log("Remove Introducer API Response:", response);

            if (response.data.statuscode === 200) {
                // API call successful - remove from local list
                setIntroducerList(prev => prev.filter((_, i) => i !== index));
                console.log("Introducer removed successfully");
            } else {
                console.error("Failed to remove introducer. Please try again.");
            }
        } catch (error) {
            console.error('Error removing introducer');
            console.error("Error removing introducer. Please try again.");
        }
    };


    const sections = [
        // 🟦 Section 1 — Header Controls
        {
            gridSize: { xs: 12, sm: 12, md: 12, lg: 12 },
            cardSx: { borderRadius: 2, borderColor: "#e0e0e0", boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)", backgroundColor: "rgba(25, 118, 210, 0.04)", borderTop: "3px solid #1976d266", transition: "box-shadow 0.2s ease-in-out", "&:hover": { boxShadow: "0 4px 10px rgba(0, 0, 0, 0.12)" } },
            inlineWithLabel: true, // ✅ new flag
            fields: [
                {
                    name: "loanType",
                    label: "Loan Type",
                    type: "select",
                    options: ["Loan", "Loan Against Deposit"],
                    size: "small",
                    gridSize: { xs: 12, sm: 3, md: 4 }, // responsive grid size
                    placeholder: "Loan Type",
                    sx: { width: '30%' },
                    onChange: (e, { form, setForm }) => {
                        const selectedValue = e.target.value;
                        setForm(prev => ({ ...prev, loanType: selectedValue }));
                    }
                },
                // Custom Loan No with prefix and suffix inside the input box
                {
                    name: "loanNo",
                    label: "Loan No",
                    type: "custom",
                    gridSize: { xs: 12, sm: 3, md: 4 },
                    render: (field, form, handleChange) => (
                        <TextField
                            name="loanNo"
                            size="small"
                            value={form.loanNo ?? ""}
                            onChange={handleChange}
                            fullWidth
                            placeholder=""
                            label="Loan No"
                            InputProps={{
                                startAdornment: <Typography variant="body1">SOC/</Typography>,
                                endAdornment: <Typography variant="body1">/2025</Typography>,
                            }}
                        />
                    ),
                },


                {
                    name: "type",
                    label: "Type",
                    type: "custom",
                    gridSize: { xs: 12, sm: 4, md: 6 },
                    render: (field, form, handleChange) => (
                        <FormControl sx={{ width: '300px', minWidth: '300px' }} size="small">
                            <InputLabel shrink={!!form.type}>Type</InputLabel>
                            <Select
                                name="type"
                                value={form.type ?? ""}
                                onChange={async (e) => {
                                    const selectedCode = e.target.value;
                                    setForm(prev => ({ ...prev, type: selectedCode }));
                                    const selectedType = loanTypes.find(lt => lt.loanTypeCode === selectedCode);
                                    if (selectedType) {
                                        try {
                                            console.log("Fetching loan type details for ID:", selectedType.id);
                                            const response = await fetchLoanTypeByIdUrl(selectedType.id);
                                            console.log("API Response of parent id on selecting:", response);
                                            if (response.data && response.data.data && response.data.data.length > 0) {
                                                const loanTypeData = response.data.data[0];
                                                setSelectedLoanType(loanTypeData);
                                                const maxLoanAmount = loanTypeData.maxLoanAmount || 0;
                                                const interestRate = loanTypeData.interestRate || 0;
                                                const maxInstallment = loanTypeData.maxInstallment || 1;
                                                const installmentAmount = calculateEMI(maxLoanAmount, interestRate, maxInstallment);
                                                setForm(prev => ({
                                                    ...prev,
                                                    loanAmount: maxLoanAmount,
                                                    interestRate: interestRate,
                                                    penalInterest: loanTypeData.penaltyIntRate || "",
                                                    rebate: loanTypeData.rebate || "",
                                                    installments: maxInstallment,
                                                    installmentAmount: installmentAmount,
                                                }));
                                                console.log("Form updated with loan type data");
                                            }
                                        } catch (error) {
                                            console.error("Error fetching loan type details:", error);
                                        }
                                    }
                                }}
                                // displayEmpty
                                label="Type"
                            >
                                <MenuItem value="" disabled>
                                    <em>Type</em>
                                </MenuItem>
                                {loanTypes.map((loanType) => (
                                    <MenuItem key={loanType.id} value={loanType.loanTypeCode}>
                                        {loanType.loanTypeCode}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ),
                },
                {
                    name: "date",
                    label: "Loan Date",
                    type: "custom",
                    gridSize: { xs: 12, sm: 6, md: 3 },
                    render: (field, form, handleChange) => (
                        <TextField
                            type="date"
                            size="small"
                            name="date"
                            label="Loan Date"
                            value={form.date ?? ""}
                            onChange={handleChange}
                            sx={{ width: '260px'}}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    ),
                },
                {
                    name: "memberNo",
                    label: "",
                    type: "custom",
                    gridSize: { xs: 12, sm: 6, md: 6 },
                    render: (field, form, handleChange) => (
                        <Box display="flex" alignItems="center" gap={1} width="100%">
                            <TextField
                                size="small"
                                name="memberNo"
                                value={form.memberNo ?? ""}
                                onChange={async (e) => {
                                    const memberNo = e.target.value;
                                    setForm(prev => ({ ...prev, memberNo }));
                                    if (memberNo && memberNo.length >= 1) { // Trigger on first character
                                        try {
                                            console.log("Fetching member details for:", memberNo);
                                            const response = await fetchMemberByMemberNumber(memberNo);
                                            console.log("Member API Response:", response);
                                            if (response.data && response.data.data) {
                                                setForm(prev => ({
                                                    ...prev,
                                                    memberName: response.data.data.fullName || ""
                                                }));
                                                console.log("Member name updated to:", response.data.data.fullName);
                                            }
                                        } catch (error) {
                                            console.error("Error fetching member details:", error);
                                        }
                                    }
                                }}
                                sx={{ flex: 1 }}
                                placeholder="Member No"
                            />
                            <TextField
                                size="small"
                                name="memberName"
                                value={form.memberName ?? ""}
                                onChange={handleChange}
                                sx={{ flex: 1 }}
                                placeholder="Member Name"
                                disabled
                            />
                        </Box>
                    ),
                },

                {
                    name: "bondNo",
                    label: "",
                    type: "custom",
                    gridSize: { xs: 12, sm: 6, md: 3 },
                    render: (field, form, handleChange) => (
                        <Box display="flex" alignItems="center" gap={1} width="100%">
                            <TextField
                                name="loanNo"
                                size="small"
                                value={form.loanNo ?? ""}
                                onChange={handleChange}
                                sx={{ flex: 1 }}
                                placeholder="Bond Number"
                            />
                        </Box>
                    ),
                },
            ],
            // 🟢 Pass button metadata - removed Edit, Search, Refresh; Voucher Print moved to footer
            buttons: [],
        },


        // 🟩 Section 2 — Member Info
        {
            title: "Member & Loan Details",
            gridSize: { xs: 12, md: 6, lg: 6 },
            cardSx: { borderRadius: 2, borderColor: "#e0e0e0", boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)", backgroundColor: "rgba(76, 175, 80, 0.04)", borderTop: "3px solid #388e3c66", transition: "box-shadow 0.2s ease-in-out", "&:hover": { boxShadow: "0 4px 10px rgba(0, 0, 0, 0.12)" } },
            fields: [
                { name: "loanAmount", label: "Loan Amount", type: "number", size: 6, disabled: true },
                { name: "interestRate", label: "Interest Rate (%)", type: "number", size: 6, disabled: true },
                { name: "installments", label: "Installments", type: "number", size: 6, disabled: true },
                { name: "installmentAmount", label: "Installment Amount", type: "number", size: 6, disabled: true },
                { name: "penalInterest", label: "Penal Int. Rate (%)", type: "number", size: 6, disabled: true },
                { name: "loanBalance", label: "Loan Balance", type: "number", size: 6 },
                { name: "purpose", label: "Purpose", size: 12 },
                {
                    name: "status",
                    label: "Status",
                    type: "select",
                    options: ["New", "Approved", "Pending", "Closed"],
                    size: 6,
                },
                { name: "rebate", label: "Rebate", type: "number", size: 6, disabled: true },
                { name: "lateFees", label: "Late Fees", type: "number", size: 6 },
                {
                    name: "accountStatus",
                    label: "A/C Status",
                    type: "select",
                    options: ["Pending", "Active", "Closed"],
                    size: 6,
                },
                { name: "loanAgainstDepositNo", label: "Loan Against Deposit No", size: 6 },

                // Introducer Details
                {
                    name: "introducerDetails",
                    label: "Introducer Details",
                    type: "custom",
                    render: (field, form, handleChange) => (
                        <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                                Introducer Details
                            </Typography>

                            {/* Introducer Details */}
                            <Grid container size={12} spacing={1} mt={1}>
                                <Grid item size={4}>
                                    <TextField
                                        fullWidth
                                        label="Introducer (No. or Name)"
                                        size="small"
                                        value={form.introducer || ""}
                                        onChange={(e) => {
                                            handleChange({ target: { name: "introducer", value: e.target.value } });
                                        }}
                                        onBlur={(e) => handleIntroducerChange(e.target.value)}
                                        disabled={!form.memberNo}
                                        InputLabelProps={{ shrink: !!form.introducer }}
                                        InputProps={{
                                            endAdornment: isLoadingIntroducer ? (
                                                <Box
                                                    sx={{
                                                        width: 16,
                                                        height: 16,
                                                        border: "2px solid #ccc",
                                                        borderTop: "2px solid transparent",
                                                        borderRadius: "50%",
                                                        animation: "spin 1s linear infinite",
                                                    }}
                                                />
                                            ) : null
                                        }}
                                    />
                                </Grid>
                                <Grid item size={3}>
                                    <TextField
                                        fullWidth
                                        label="Mobile No"
                                        size="small"
                                        value={form.introducerMobile || ""}
                                        onChange={(e) => handleChange({ target: { name: "introducerMobile", value: e.target.value } })}
                                        disabled={!form.memberNo}
                                        InputLabelProps={{ shrink: !!form.introducerMobile }}
                                    />
                                </Grid>
                                <Grid item size={4}>
                                    <Select
                                        fullWidth
                                        displayEmpty
                                        size="small"
                                        value={form.introducerRelation || ""}
                                        onChange={(e) => handleChange({ target: { name: "introducerRelation", value: e.target.value } })}
                                        disabled={!form.memberNo}
                                        renderValue={(value) => value || "Select Relation"}
                                    >
                                        <MenuItem value="" disabled>
                                            Select Relation
                                        </MenuItem>
                                        <MenuItem value="Father">Father</MenuItem>
                                        <MenuItem value="Mother">Mother</MenuItem>
                                        <MenuItem value="Son">Son</MenuItem>
                                        <MenuItem value="Daughter">Daughter</MenuItem>
                                        <MenuItem value="Spouse">Spouse</MenuItem>
                                        <MenuItem value="Brother">Brother</MenuItem>
                                        <MenuItem value="Sister">Sister</MenuItem>
                                    </Select>
                                </Grid>
                                <Grid item size={1}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={addIntroducerToList}
                                        disabled={!form.memberNo || !form.introducer}
                                        sx={{
                                            minWidth: 'auto',
                                            width: '100%',
                                            height: '40px',
                                            backgroundColor: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            },
                                            '&:disabled': {
                                                backgroundColor: '#ccc',
                                                color: '#666'
                                            }
                                        }}
                                    >
                                        <Add sx={{ fontSize: '20px' }} />
                                    </Button>
                                </Grid>
                            </Grid>

                            {/* Introducer Table */}
                            {introducerList.length > 0 && (
                                <Box sx={{ mt: 2, width: '100%' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                        Introducer List
                                    </Typography>
                                    <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                        No.
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                        Name
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                        Mobile
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                        Relation
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px' }}>
                                                        Action
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {introducerList.map((introducer, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{introducer.introducerNo}</TableCell>
                                                        <TableCell>{introducer.introducerName}</TableCell>
                                                        <TableCell>{introducer.introducerMobile}</TableCell>
                                                        <TableCell>{introducer.introducerRelation}</TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeIntroducerFromList(index)}
                                                                sx={{
                                                                    color: 'error.main',
                                                                    '&:hover': {
                                                                        backgroundColor: 'error.light',
                                                                        color: 'white'
                                                                    }
                                                                }}
                                                            >
                                                                <Close sx={{ fontSize: '16px' }} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </Box>
                    ),
                },
            ],
        },

        // 🟨 Section 3 — Deposit Info
        {
            title: "Deposit Details",
            gridSize: { xs: 12, md: 5, lg: 5 },
            cardSx: { borderRadius: 2, borderColor: "#e0e0e0", boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)", backgroundColor: "rgba(245, 124, 0, 0.04)", borderTop: "3px solid #f57c0066", transition: "box-shadow 0.2s ease-in-out", "&:hover": { boxShadow: "0 4px 10px rgba(0, 0, 0, 0.12)" } },
            disabled: (form) => form.loanType === "Loan",
            fields: [
                { name: "depositNo", label: "Deposit No", size: 6, disabled: (form) => form.loanType === "Loan" },
                { name: "amount", label: "Amount", type: "number", size: 6, disabled: (form) => form.loanType === "Loan" },
                {
                    name: "depositTable",
                    label: "Deposits",
                    type: "custom",
                    render: () => (
                        <Box sx={{
                            maxWidth: { xs: '100%', sm: '350px', md: '580px' },
                            // width: '100%',
                            overflowX: 'auto'
                        }}>
                            <EnhancedTable
                                title="Deposits"
                                data={depositData}
                                columns={depositColumns}
                                searchable={false}
                                paginated={false}
                                dense
                                hideRowsPerPageOptions
                                showRowNumbers
                                sx={{
                                    '& .MuiTableCell-root': {
                                        padding: '1px 1px',
                                        fontSize: '0.875rem'
                                    },
                                    '& .MuiTableHead-root .MuiTableCell-root': {
                                        padding: '1px 1px',
                                        fontWeight: 600
                                    }
                                }}
                            />
                        </Box>
                    ),
                },
            ],
        },

        // 🟧 Section 4 — Payment Info + Remarks
        {
            title: "Payment & Remarks",
            gridSize: { xs: 12, md: 12, lg: 12 },
            cardSx: { borderRadius: 2, borderColor: "#e0e0e0", boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)", backgroundColor: "rgba(211, 47, 47, 0.04)", borderTop: "3px solid #d32f2f66", transition: "box-shadow 0.2s ease-in-out", "&:hover": { boxShadow: "0 4px 10px rgba(0, 0, 0, 0.12)" } },
            fields: [
                { name: "paymentNo", label: "Payment No", size: 6 },
                {
                    name: "paymentMode",
                    label: "Payment Mode",
                    type: "select",
                    options: [
                        { value: "CASH", label: "Cash" },
                        { value: "CHEQUE", label: "Cheque" },
                        { value: "NEFT/RTGS", label: "NEFT/RTGS" },
                        { value: "UPI", label: "UPI" },
                    ],
                    size: 6,
                    onChange: (e, { form, setForm }) => {
                        const selectedValue = e.target.value;
                        setForm(prev => ({ ...prev, paymentMode: selectedValue }));
                    }
                },
                {
                    name: "chequeNo",
                    label: "Cheque No / NEFT / RTGS",
                    type: "custom",
                    size: 6,
                    render: (field, form, handleChange) => (
                        <TextField
                            size="small"
                            name="chequeNo"
                            label="Cheque No / NEFT / RTGS"
                            value={form.chequeNo ?? ""}
                            onChange={handleChange}
                            fullWidth
                            disabled={form.paymentMode === "CASH"}  // ✅ Disable when Cash is selected
                        />
                    ),
                },
                {
                    name: "chequeDate",
                    label: "Cheque Date",
                    type: "custom",
                    size: 6,
                    render: (field, form, handleChange) => (
                        <TextField
                            type="date"
                            size="small"
                            name="chequeDate"
                            label="Cheque Date"
                            value={form.chequeDate ?? ""}
                            onChange={handleChange}
                            fullWidth
                            disabled={form.paymentMode === "CASH"}
                            InputLabelProps={{
                                shrink: true, // ✅ keeps label always visible
                            }}
                        />
                    ),
                },
                {
                    name: "narration",
                    label: "Narration",
                    type: "custom",
                    render: (field, form, handleChange) => (
                        <TextField
                            size="small"
                            name="narration"
                            label="Narration"
                            multiline
                            rows={2}
                            value={form.narration ?? ""}
                            onChange={handleChange}
                            fullWidth
                            disabled={form.paymentMode === "CASH"} // ✅ Disable when cash is selected
                        />
                    ),
                },
                {
                    name: "otherRemarks",
                    label: "Other Remarks",
                    type: "custom",
                    render: (field, form, handleChange) => (
                        <TextField
                            size="small"
                            name="otherRemarks"
                            label="Other Remarks"
                            multiline
                            rows={2}
                            value={form.otherRemarks ?? ""}
                            onChange={handleChange}
                            fullWidth
                            disabled={form.paymentMode === "CASH"} // ✅ Disable when cash is selected
                        />
                    ),
                },
            ],
        },
    ];

    return (
        <DynamicFormModal
            open={open}
            onClose={onClose}
            onSave={onSave}
            title="Loan"
            sections={sections}
            initialForm={form}
            sx={{
                '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                },
            }}
        />
    );
};

export default IssueLoanModalForm;

